/**
 * Release Config Service (CDN-based)
 *
 * Fetches per-RID config from CDN, determines 3 version states via signals,
 * and writes to IDB for SettingsService feature flag compat.
 *
 * Replaces the old UpdateDetectionService which used Cloud Functions + RTDB.
 *
 * Trigger points:
 * - Constructor (initial check)
 * - visibilitychange (tab becomes visible)
 * - 60-second throttle between checks
 *
 * Schema resilience: If CDN returns anything that can't be cleanly parsed,
 * the entire response is ignored and the app continues on bundled defaults.
 */

import { Injectable, Injector, OnDestroy, signal } from '@angular/core';
import { AppVersionStatus } from '@app/_types/app-version-status.type';
import { LOCAL_RELEASE_CONFIG, ENTITY_SET_VERSION } from '@app/_configs/entity-set-version';
import { environment } from '@environments/environment';
import { IndexDbService } from './index-db.service';
import { log } from '@app/_services/logger';

@Injectable({
  providedIn: 'root',
})
export class ReleaseConfigService implements OnDestroy {
  /** Current version status — drives UI (banner/blocker) */
  readonly versionStatus = signal<AppVersionStatus>('supported');

  /** Raw CDN config for the current environment (null until first successful fetch) */
  readonly config = signal<any>(null);

  /** Whether the deprecated banner was dismissed this session */
  readonly isDismissed = signal(false);

  private lastCheckTime = 0;
  private readonly THROTTLE_MS = 60_000; // 60 seconds between checks
  private readonly visibilityHandler: () => void;

  /** Session storage key for dismissed state */
  private static readonly DISMISSED_KEY = 'mevolve_rc_dismissed';

  constructor(
    private idbService: IndexDbService,
    private injector: Injector,
  ) {
    // Restore dismissed state from session storage
    this.isDismissed.set(sessionStorage.getItem(ReleaseConfigService.DISMISSED_KEY) === 'true');

    // Initial check
    this.checkConfig();

    // Re-check when tab becomes visible
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.checkConfig();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Fetch config from CDN. Entire method is wrapped in try/catch for schema resilience.
   * On any failure: log error, keep current state (default: supported).
   */
  async checkConfig(): Promise<void> {
    // Throttle: skip if last check was < 60s ago
    const now = Date.now();
    if (now - this.lastCheckTime < this.THROTTLE_MS) return;
    // Set throttle time upfront so failures are also throttled
    // and concurrent calls from rapid visibilitychange events are suppressed
    this.lastCheckTime = now;

    try {
      // 1. Fetch from per-env R2 CDN bucket
      // Cache-busting: R2 CDN returns ETag but no Cache-Control header,
      // so browsers may cache aggressively and return stale data.
      const cdnBase = environment.cfPublicR2Base;
      if (!cdnBase) {
        log.warn('ReleaseConfigService', 'RC check skipped: cfPublicR2Base is empty');
        return;
      }
      const rid = LOCAL_RELEASE_CONFIG.rid;
      const url = `${cdnBase}/config/${rid}.json?_=${Date.now()}`;

      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        log.warn('ReleaseConfigService', `CDN fetch failed: HTTP ${response.status}`, { url });
        return;
      }

      // 2. Parse defensively — entire parse in try/catch
      let root: any;
      let env: any;
      try {
        root = await response.json();
        env = root;
        if (!env?.status) {
          log.warn('ReleaseConfigService', 'CDN response missing status field', { rid });
          return;
        }
      } catch (parseError) {
        log.error('ReleaseConfigService', 'CDN JSON parse FAILED — using bundled defaults', parseError as Error);
        return;
      }

      // 3. Update config signal
      this.config.set(env);

      // 4. Write to IDB for SettingsService compat (maps clean CDN param names → _n keys)
      try {
        const reconstructed = {
          id: LOCAL_RELEASE_CONFIG.id,
          rid: root.rid,
          entitySetVersion: root.entitySetVersion,
          externalDescription: {},
          envs: {
            [environment.env]: {
              featureFlags: root.featureFlags ?? {},
              enabledSegments: root.enabledSegments ?? [],
              enabledPlatforms: root.enabledPlatforms ?? [],
              parameters: {
                translationsVersion_n: root.parameters?.translationsVersion ?? 0,
                gracePeriodInMin_n: root.parameters?.gracePeriodInMin ?? 0,
                defaultThemeMode: root.parameters?.defaultThemeMode ?? '',
                defaultThemeColor: root.parameters?.defaultThemeColor ?? '',
              },
            },
          },
        };
        await this.idbService.bulkPut('releaseConfigs', [reconstructed]);
        log.info('ReleaseConfigService', 'Saved CDN config to IDB', { rid: root.rid });
      } catch (idbError) {
        log.warn('ReleaseConfigService', 'Failed to write RC to IDB', idbError as Error);
      }

      // 5. Determine status and update signal
      const status = this.determineStatus(env, root);
      this.versionStatus.set(status);

      log.info('ReleaseConfigService', 'Config check complete', {
        rid: root.rid,
        envStatus: env.status,
        entitySetVersion: root.entitySetVersion,
        localEntitySetVersion: ENTITY_SET_VERSION,
        determinedStatus: status,
      });

    } catch (e) {
      log.error('ReleaseConfigService', 'RC check failed entirely — using bundled defaults', e as Error);
    }
  }

  /**
   * Determine version status from CDN config.
   *
   * Priority:
   * 1. env.status === 'unsupported' → unsupported
   * 2. minBuildNumber > current build → unsupported
   * 3. env.status === 'deprecated' → deprecated
   * 4. Otherwise → supported
   */
  private determineStatus(env: any, _root: any): AppVersionStatus {
    // Explicit unsupported status from CDN
    if (env.status === 'unsupported') return 'unsupported';

    // minBuildNumber — force update if current build is behind minimum
    if (env.minBuildNumber != null && env.minBuildNumber > 0) {
      const currentBuild = this.getCurrentBuildNumber();
      if (currentBuild < env.minBuildNumber) {
        log.warn('ReleaseConfigService',
          `Build ${currentBuild} < minBuildNumber ${env.minBuildNumber} → unsupported`);
        return 'unsupported';
      }
    }

    // Deprecated (dismissable banner)
    if (env.status === 'deprecated') return 'deprecated';

    // All good
    return 'supported';
  }

  /**
   * Get current build number from environment.
   * buildVersion is set by CI (GitHub Actions run_number). Falls back to 0 for local dev.
   */
  private getCurrentBuildNumber(): number {
    const raw = environment.buildVersion;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Dismiss the deprecated banner for this session.
   */
  dismissDeprecated(): void {
    log.info('ReleaseConfigService', 'Deprecated banner dismissed by user');
    this.isDismissed.set(true);
    sessionStorage.setItem(ReleaseConfigService.DISMISSED_KEY, 'true');
  }

  /**
   * Force update: clear sensitive data + delete IDB + logout + redirect to /login.
   *
   * CRITICAL — Loop prevention:
   * /login page does NOT call checkConfig() → no loop possible.
   * Even if IDB deletion is blocked by other tabs, the redirect still happens.
   */
  async forceUpdate(): Promise<void> {
    log.warn('ReleaseConfigService', 'Force update initiated — clearing local data and redirecting');

    try {
      // 1. Preserve user preferences (appTheme, appLanguageCode) via clearSensitiveData
      const { StorageService } = await import('./storage.servive');
      const storageService = this.injector.get(StorageService);
      storageService.clearSensitiveData();

      // 2. Delete IndexedDB — MUST await all outcomes (success, error, blocked)
      await new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase('mevolveDb');
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();    // proceed even if fails
        req.onblocked = () => resolve();  // proceed even if blocked by other tabs
      });

      // 3. Sign out
      const { AuthService } = await import('./auth.service');
      const authService = this.injector.get(AuthService);
      await authService.logoutUser();
    } catch (error) {
      log.error('ReleaseConfigService', 'Error during force update cleanup', error as Error);
    }

    // 4. Redirect to /login (NOT reload — prevents infinite loop)
    window.location.href = '/login';
  }
}
